// src/app/services/auth/auth.service.ts (CMS)

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, switchMap, tap } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ILogin } from '../../interfaces/ILogin';
import { createCmsJwt } from '../../utils/jwt-app.utils';

export interface IAppLoginApiResponse {
  status: 'SUCCESS' | 'ERROR' | string;
  message: string;
  content?: any;
  type?: string;
  code?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'app_token';
  private readonly CMS_TOKEN_KEY = 'cms_token';
  private readonly AUTH_RESPONSE_KEY = 'app_auth_response';

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  login(credentials: ILogin): Observable<IAppLoginApiResponse> {
    return this.loginCms(credentials);
  }

  private createCmsLoginJwt(credentials: ILogin): Promise<string> {
    const payload = {
      email: credentials.username,
      password: credentials.password,
    };

    const secret = environment.jwtSecretKeyCms;

    return createCmsJwt(payload, secret);
  }

  private loginCms(credentials: ILogin): Observable<IAppLoginApiResponse> {
    const url = `${environment.apiUrl}/public/cms/authentication/login`;

    return from(this.createCmsLoginJwt(credentials)).pipe(
      switchMap((jwt: string) => {
        const body = { payload: jwt };
        console.log('[AuthService CMS] body enviado ao login:', body);
        return this.http.post<IAppLoginApiResponse>(url, body);
      }),
      tap((response: IAppLoginApiResponse) => {
        console.log('[AuthService CMS] resposta de login:', response);
        this.handleLoginSuccess(response);

        const cmsToken = response?.content?.token;
        if (cmsToken) {
          localStorage.setItem(this.CMS_TOKEN_KEY, String(cmsToken));
        }
      })
    );
  }

  private handleLoginSuccess(response: IAppLoginApiResponse): void {
    localStorage.setItem(
      this.AUTH_RESPONSE_KEY,
      JSON.stringify(response)
    );

    const content = response.content as any;

    const possibleToken =
      content?.token ||
      content?.access_token ||
      content?.jwt ||
      content?.jwtToken ||
      content?.authToken;

    if (possibleToken) {
      localStorage.setItem(this.TOKEN_KEY, String(possibleToken));
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getCmsToken(): string | null {
    return localStorage.getItem(this.CMS_TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    const token = this.getCmsToken() || this.getToken();

    if ((environment as any).skipAuth) {
      return !!token;
    }

    if (!token) {
      return false;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      const payloadBase64 = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);

      const exp = payload.exp;
      if (!exp) {
        return true;
      }

      const nowInSeconds = Math.floor(Date.now() / 1000);
      return nowInSeconds < exp;
    } catch (e) {
      console.error('[AuthService] erro ao decodificar token:', e);
      return false;
    }
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.CMS_TOKEN_KEY);
    localStorage.removeItem(this.AUTH_RESPONSE_KEY);
    this.router.navigate(['/login']);
  }
}
